import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { hockeyFormatter } from '@nhl/formatter-utilities';
import { ReactTable, SelectTypes } from '@nhl/nhl-breakaway-ui';

import CommonMessages from '~/core/commonMessages';

import { usePlayerGameLogQuery } from '../../api';
import { StyledSelect } from '../../styles';
import { GameLogPayload, PlayerStatsSeason } from '../../types';
import { createShortGameTypeOption } from '../utils';

import { gameLogsColumnsForGoalies, gameLogsColumnsForSkaters } from './columns';

import { EmptyTableMessage, LabelContainer, PlayerTableContainer, SelectContainer } from './styles';

type PlayerGameLogsProps = {
    gameLog: GameLogPayload;
    isGoalie: boolean;
    playerId: string | undefined;
};

export const PlayerGameLogs: FC<PlayerGameLogsProps> = ({ gameLog, playerId, isGoalie }) => {
    const [selectedSeason, setSelectedSeason] = useState<PlayerStatsSeason>();
    const [selectedGameType, setSelectedGameType] = useState(gameLog?.gameTypeId);
    const intl = useIntl();

    const findPlayerStatsSeasonForSeason = useCallback(
        (seasonId: number) => gameLog?.playerStatsSeasons?.find((s) => s.season === seasonId),
        [gameLog]
    );

    useEffect(() => {
        setSelectedSeason(findPlayerStatsSeasonForSeason(gameLog?.seasonId));
    }, [findPlayerStatsSeasonForSeason, gameLog]);

    const isEnabled =
        playerId !== undefined &&
        (gameLog.seasonId !== selectedSeason?.season || gameLog.gameTypeId !== selectedGameType);
    const { data, isLoading } = usePlayerGameLogQuery(playerId, selectedSeason?.season, selectedGameType, isEnabled);

    const localGameLog = isEnabled ? data : gameLog;

    const seasonOptions: Array<SelectTypes.BasicOption> = useMemo(() => {
        const seasons = gameLog.playerStatsSeasons?.map((obj) => obj.season) || [];
        const seasonsSet = new Set(seasons); // ensures no duplicate seasons caused by players switching teams midseason

        return Array.from(seasonsSet).map((obj, i) => ({
            key: String(i),
            label: hockeyFormatter.singleSeasonFormatter(obj),
            value: obj,
        }));
    }, [gameLog?.playerStatsSeasons]);

    const gameTypeOptions: Array<SelectTypes.BasicOption> = useMemo(
        () => selectedSeason?.gameTypes?.map((gameType) => createShortGameTypeOption(intl, gameType)) || [],
        [intl, selectedSeason?.gameTypes]
    );

    const selectLabels = useMemo(
        () => ({
            gameType: {
                ariaLabels: {
                    dropdownButton: intl.formatMessage(CommonMessages.ariaLabels.select.gameType.dropdownButton),
                    optionsBox: intl.formatMessage(CommonMessages.ariaLabels.select.season.optionsBox),
                },
                placeholder: intl.formatMessage({
                    description: "An abbreviation for 'not available'",
                    defaultMessage: 'N/A',
                }),
            },
            season: {
                dropdownButton: intl.formatMessage(CommonMessages.ariaLabels.select.season.dropdownButton),
                optionsBox: intl.formatMessage(CommonMessages.ariaLabels.select.season.optionsBox),
            },
        }),
        [intl]
    );

    if (
        gameTypeOptions.length > 0 &&
        selectedGameType &&
        !gameTypeOptions?.find((opt) => opt.value === selectedGameType)
    ) {
        setSelectedGameType(Number(gameTypeOptions[0]?.value));
    }

    return isLoading ? null : (
        <>
            <LabelContainer>
                <h4 aria-level={3}>
                    <FormattedMessage {...CommonMessages.tableHeaders.long['gameLogs']} />
                </h4>

                <SelectContainer>
                    <StyledSelect
                        id="season-select"
                        dropdownButtonAriaLabel={selectLabels.season.dropdownButton}
                        onOptionChange={(_, value: SelectTypes.BasicOption) =>
                            setSelectedSeason(findPlayerStatsSeasonForSeason(value.value as number))
                        }
                        options={seasonOptions}
                        optionsBoxAriaLabel={selectLabels.season.optionsBox}
                        selectedOption={
                            selectedSeason
                                ? seasonOptions.find((option) => option.value === selectedSeason.season)
                                : null
                        }
                    />
                    <StyledSelect
                        id="game-type-select"
                        dropdownButtonAriaLabel={selectLabels.gameType.ariaLabels.dropdownButton}
                        onOptionChange={(_, value: SelectTypes.BasicOption) =>
                            setSelectedGameType(value.value as number)
                        }
                        options={gameTypeOptions}
                        isSelectDisabled={gameTypeOptions.length === 0}
                        optionsBoxAriaLabel={selectLabels.gameType.ariaLabels.optionsBox}
                        placeholder={selectLabels.gameType.placeholder}
                        selectedOption={
                            selectedGameType
                                ? gameTypeOptions.find((option) => option.value === selectedGameType)
                                : null
                        }
                    />
                </SelectContainer>
            </LabelContainer>
            <PlayerTableContainer>
                {localGameLog?.gameLog?.length > 0 ? (
                    <ReactTable
                        data={localGameLog?.gameLog}
                        columns={isGoalie ? gameLogsColumnsForGoalies(intl) : gameLogsColumnsForSkaters(intl)}
                        tableId="game-log-table"
                    />
                ) : (
                    <EmptyTableMessage>
                        <FormattedMessage
                            description="A message for when there is no game log data available for the player"
                            defaultMessage="Player has not yet played in an NHL game this season"
                        />
                    </EmptyTableMessage>
                )}
            </PlayerTableContainer>
        </>
    );
};
